import { Grid } from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import ApplicationTable from '../../components/applicationTable/applicationTable';
import blueCircle from '../../images/dashboard/blue circle.png';
import greenCircle from '../../images/dashboard/green  circle.png';
import redCircle from '../../images/dashboard/red circle.png';
import './childCard.scss';
/**
 * ChildCard Component
 * 
 * This component renders detailed information about a child entity (Circle, Division, Sub-Division, etc.)
 * including performance metrics, a pie chart, and application tables.
 *
 * @component
 * @memberof Widgets
 * @param {Object} props - The component props.
 * @param {Object} props.childInfo - The information about the child entity.
 * @param {string} props.childInfo.name - The name of the child entity.
 * @param {number} props.childInfo.level - The hierarchy level of the entity (e.g., 3 for Circle, 4 for Division, 5 for Sub-Division).
 * @param {string} props.childInfo.selfIcon - The URL of the entity's icon.
 * @param {number} props.childInfo.overallPerformance - The performance percentage of the entity.
 * @param {string} props.childInfo.trendingIcon - The URL of the trending icon.
 * @param {Function} props.childInfo.drillFunction - Function to navigate to a lower level in the hierarchy.
 * @param {string} props.childInfo.childrenIcon - The URL of the children icon.
 * @param {string} props.childInfo.code - The office code of the entity.
 * @param {Array<Object>} props.childInfo.tableDataServices - Data for the Customer Services table.
 * @param {Array<Object>} props.childInfo.tableDataTechnical - Data for the Technical & Commercial Losses table.
 * 
 * @returns {JSX.Element} The rendered ChildCard component.
 */
const ChildCard = ({ childInfo }) => {
    const navigate = useNavigate();
    const tableColumnsServices = [
        {
            header: 'Type',
            accessorKey: 'type',
        },
        {
            header: 'Pending',
            accessorKey: 'pending',
        },
        {
            header: 'Attended',
            accessorKey: 'attended',
        },
        {
            header: 'Total',
            accessorKey: 'total',
        },
    ]

    const tableColumnsTechnical = [
        {
            header: 'Type',
            accessorKey: 'type',
        },
        {
            header: 'Monthly',
            accessorKey: 'monthly',
        },
        {
            header: 'Progressive',
            accessorKey: 'progressive',
        },
    ]

    const NewConnectionPieChartTop = {
        chart: {
            type: 'pie',
            plotBorderWidth: 0,
            backgroundColor: 'transparent',
            height: 45,
            margin: [0, 0, 0, 0],
            width: 45
        },
        title: {
            text: '',
            style: {
                fontSize: '14px'
            }
        },
        tooltip: {
            enabled: false,
            pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>'
        },
        plotOptions: {
            pie: {
                allowPointSelect: false,
                cursor: 'pointer',
                dataLabels: {
                    enabled: false,
                    format: `{point.percentage:.1f} %`,
                    distance: -50, // Set the distance to move the labels inside the pie chart
                    style: {
                        color: 'white', // Set the color of the data labels to white
                        textOutline: 'none', // Remove text outline if any
                        fontSize: 17.5,
                        fontWeight: 'normal'
                    }
                },
                showInLegend: false
            }
        },
        colors: [ // Custom colors for the pie slices
            '#12ae6c', // Slice 1 color
            '#FF4A4A', // Slice 2 color
            '#5368F0', // Slice 3 color
            // Add more colors as needed
        ],
        legend: {
            align: 'left',
            verticalAlign: 'bottom',
            layout: 'horizontal',
            itemMarginBottom: 0, // Optional: You can adjust the spacing between legend items
            x: 0,
            y: 0,
            symbolRadius: 2,
        },
        series: [{
            name: 'Applications',
            colorByPoint: true,
            data: [{
                name: 'Installed',
                y: 40.6,
            }, {
                name: 'Ripe',
                y: 28.1,
            }, {
                name: 'Pending',
                y: 31.3
            }]
        }],
        credits: {
            enabled: false
        },
    }

    return (
        <Grid container padding={1} className='child-card-container'>
            <Grid item className='main-content-item'>
                <Grid container padding={1} spacing={1} className='main-content'>
                    <Grid item className='name-title-item'>
                        <Grid container spacing={1} className='name-title-left-side'>
                            <Grid item className='self-icon-item'>
                                <img src={childInfo.selfIcon} alt='circle icon' className='self-icon' />
                            </Grid>
                            <Grid item>
                                <h3>
                                    {childInfo.name} {childInfo.level === 3 ? 'CIRCLE' : childInfo.level === 4 ? 'DIVISION' : childInfo.level === 5 ? 'SUB-DIVISION' : ''}
                                </h3>
                            </Grid>
                            <Grid item className='child-performance-item'>
                                <div className='child-performance-container' style={{ backgroundColor: childInfo.overallPerformance > 0 ? '#e11b3c' : '#019760' }}>
                                    <img src={childInfo.trendingIcon} className='child-performance-icon' alt="trending" />
                                    <h2 className='child-performance-text'>
                                        {childInfo.overallPerformance ? Math.abs(childInfo.overallPerformance) : ''}%
                                    </h2>
                                </div >
                            </Grid>
                            <Grid item className='card-item'>
                                <div className='card-container'>
                                    <p className='card-text'>
                                        New <br /> Connection
                                    </p>
                                    <HighchartsReact
                                        highcharts={Highcharts}
                                        options={NewConnectionPieChartTop}
                                    />
                                </div >
                            </Grid>
                        </Grid>
                    </Grid>
                    {
                        childInfo.level < 5 ?
                            <Grid item className='child-button-item' onClick={() => childInfo.drillFunction(childInfo)}>
                                <div className='child-button-container'>
                                    <img src={childInfo.childrenIcon} alt="trending" className='children-icon' />
                                    <p className='children-title' >
                                        {
                                            childInfo.level === 3 ? 'DIVISIONS' :
                                                childInfo.level === 4 ? 'SUB-DIVISIONS' :
                                                    ''
                                        }
                                    </p>
                                </div >
                            </Grid> :

                            <Grid item className='child-button-item' onClick={() => navigate('/homefieldoffice', { state: { officeCode: childInfo.code } })}>
                                <div className='child-button-container'>
                                    <p className='children-title' >
                                        Details
                                    </p>
                                </div >
                            </Grid>
                    }
                </Grid>
            </Grid>

            <Grid item className='bottom-cards-item'>
                <hr style={{ margin: '1%' }} />
                <Grid container padding={1} spacing={2} className='bottom-cards-container'>
                    <Grid item lg={7.5} className='card-item'>
                        <div className='card-container'>
                            <h4 className='card-title'>Customer Services</h4>
                            <div className='table-container'>
                                <ApplicationTable data={childInfo.tableDataServices} columns={tableColumnsServices} type="services" pendingIcon={redCircle} attendedIcon={greenCircle} totalIcon={blueCircle} />
                            </div>
                        </div>
                    </Grid>
                    <Grid item lg={4.5} className='card-item'>
                        <div className='card-container'>
                            <h4 className='card-title'>Technical & Commercial Losses</h4>
                            <div className='table-container'>
                                <ApplicationTable data={childInfo.tableDataTechnical} columns={tableColumnsTechnical} type="technical" pendingIcon={redCircle} />
                                {/* <div className='trending-icon-container'>
                                    <img src={childInfo.trendingIcon} alt='trending' className='trending-icon'/>
                                </div>
                                <p className='trending-text'>
                                    Trending
                                </p> */}
                            </div>
                        </div>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    )
}

export default ChildCard
